import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Factory, Loader2, ExternalLink } from "lucide-react";

export const ESTADO_PARTIDA = {
  pendiente: { label: "Pendiente", cls: "bg-zinc-700/60 text-zinc-200 border-zinc-600" },
  aceptada: { label: "Aceptada", cls: "bg-emerald-500/15 text-emerald-200 border-emerald-500/40" },
  pospuesta: { label: "Pospuesta", cls: "bg-amber-500/15 text-amber-200 border-amber-500/40" },
  rechazada: { label: "Rechazada", cls: "bg-red-500/15 text-red-200 border-red-500/40" },
  convertida_peticion: { label: "En petición", cls: "bg-violet-500/15 text-violet-200 border-violet-500/40" },
};
const fmt = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);
const imp = (it) => (parseFloat(it.cantidad) || 0) * (parseFloat(it.precio_unitario) || 0);

/** Aprobación parcial por partidas: estados, selección múltiple y CTA «Pasar selección a Petición de Taller». */
export function PartidasAprobacionPanel({ p, onChanged, canEdit }) {
  const nav = useNavigate();
  const [sel, setSel] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const items = p.items || [];
  const peticiones = useMemo(() => Object.fromEntries((p.peticiones || []).map((x) => [x.id, x])), [p.peticiones]);
  const est = (it) => it.estado || "pendiente";
  const elegibles = items.filter((it) => !["convertida_peticion", "rechazada"].includes(est(it)));
  const selItems = items.filter((it) => sel.includes(it.id));
  const totalSel = selItems.reduce((a, it) => a + imp(it), 0);
  const restantes = items.filter((it) => !sel.includes(it.id) && ["pendiente", "aceptada", "pospuesta"].includes(est(it)));
  const bloqueado = p.estado === "rechazado" || p.estado === "caducado" || p.estado === "anulado_por_cliente";
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const marcar = async (estado) => {
    if (!sel.length) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/presupuestos/${p.id}/partidas-estado`, { item_ids: sel, estado });
      toast.success(`${sel.length} partida(s) → ${ESTADO_PARTIDA[estado].label}${data.lead_estado ? ` · lead «${data.lead_estado}»` : ""}`);
      setSel([]); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cambiar el estado"); } finally { setBusy(false); }
  };
  const convertir = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/presupuestos/${p.id}/convertir-parcial`, { item_ids: sel, aceptar: true });
      toast.success(`Petición ${data.peticion_codigo} creada (pendiente de validación)`);
      setConfirm(false); setSel([]); onChanged?.();
      nav(`/peticiones/${data.peticion_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo convertir"); } finally { setBusy(false); }
  };
  if (!items.length) return null;
  const nConv = items.filter((it) => est(it) === "convertida_peticion").length;
  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 mb-5" data-testid="partidas-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="text-[11px] uppercase tracking-wider text-violet-300 font-semibold flex items-center gap-1.5">
          <Factory className="w-3.5 h-3.5" /> Aprobación por partidas
          <span className="text-zinc-500 normal-case tracking-normal font-normal">· {nConv}/{items.length} en petición{(p.peticiones || []).length > 1 ? ` · ${p.peticiones.length} peticiones` : ""}</span>
        </div>
        {canEdit && !bloqueado && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <Button size="sm" variant="outline" disabled={!sel.length || busy} onClick={() => setSel([])} className="bg-transparent border-zinc-700 h-8 text-xs">Limpiar</Button>
            <Button size="sm" variant="outline" disabled={!sel.length || busy} onClick={() => marcar("aceptada")} className="bg-transparent border-emerald-500/40 text-emerald-200 h-8 text-xs" data-testid="partidas-marcar-aceptada">Aceptada</Button>
            <Button size="sm" variant="outline" disabled={!sel.length || busy} onClick={() => marcar("pospuesta")} className="bg-transparent border-amber-500/40 text-amber-200 h-8 text-xs" data-testid="partidas-marcar-pospuesta">Pospuesta</Button>
            <Button size="sm" variant="outline" disabled={!sel.length || busy} onClick={() => marcar("rechazada")} className="bg-transparent border-red-500/40 text-red-200 h-8 text-xs" data-testid="partidas-marcar-rechazada">Rechazada</Button>
            <Button size="sm" disabled={!sel.length || busy} onClick={() => setConfirm(true)} className="bg-violet-500 text-zinc-950 hover:bg-violet-400 h-8 text-xs font-semibold" data-testid="partidas-convertir-btn">
              <Factory className="w-3.5 h-3.5 mr-1" /> Pasar selección a Petición de Taller
            </Button>
          </div>
        )}
      </div>
      <div className="divide-y divide-zinc-800/70">
        {items.map((it) => {
          const e = est(it);
          const conv = e === "convertida_peticion";
          const pet = it.peticion_id ? peticiones[it.peticion_id] : null;
          const puede = canEdit && !bloqueado && !conv && e !== "rechazada";
          return (
            <label key={it.id} className={`flex items-center gap-3 py-2 min-h-[44px] ${puede ? "cursor-pointer" : "opacity-80"}`} data-testid={`partida-row-${it.id}`}>
              <input type="checkbox" className="accent-violet-500 w-4 h-4 shrink-0" disabled={!puede} checked={sel.includes(it.id)} onChange={() => toggle(it.id)} data-testid={`partida-check-${it.id}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-100 truncate">{it.descripcion || "—"}</div>
                <div className="text-[11px] text-zinc-500">{it.cantidad} {it.unidad || "ud"} × {fmt(it.precio_unitario)} = <span className="text-zinc-300">{fmt(imp(it))}</span></div>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border shrink-0 ${ESTADO_PARTIDA[e]?.cls || ESTADO_PARTIDA.pendiente.cls}`} data-testid={`partida-estado-${it.id}`}>{ESTADO_PARTIDA[e]?.label || e}</span>
              {conv && it.peticion_id && (
                <button type="button" onClick={(ev) => { ev.preventDefault(); nav(`/peticiones/${it.peticion_id}`); }} data-testid={`partida-peticion-link-${it.id}`}
                  className="text-[11px] font-mono text-amber-300 hover:underline shrink-0 flex items-center gap-1">{pet?.codigo || "petición"} <ExternalLink className="w-3 h-3" /></button>
              )}
            </label>
          );
        })}
      </div>
      {sel.length > 0 && <div className="text-xs text-zinc-400 mt-2" data-testid="partidas-seleccion-total">{sel.length} seleccionada(s) · {fmt(totalSel)} (sin IVA)</div>}

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg" data-testid="partidas-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Pasar selección a Petición de Taller</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              {p.cliente_snapshot?.razon_social || "Cliente"} · {p.numero}{p.referencia ? ` · ${p.referencia}` : ""}. La petición nace <b>pendiente de validación</b>: no entra en producción hasta «Validar y lanzar a taller».
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-violet-300 mb-1">Se convierten ({selItems.length}) · {fmt(totalSel)} sin IVA</div>
              <ul className="space-y-0.5">{selItems.map((it) => <li key={it.id} className="flex justify-between gap-2"><span className="truncate">{it.descripcion}</span><span className="text-zinc-400 shrink-0">{fmt(imp(it))}{est(it) !== "aceptada" && <span className="text-[10px] text-emerald-300 ml-1">→ se marca aceptada</span>}</span></li>)}</ul>
            </div>
            {restantes.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Quedan en el presupuesto ({restantes.length}) · {fmt(restantes.reduce((a, it) => a + imp(it), 0))}</div>
                <ul className="space-y-0.5 text-zinc-400">{restantes.map((it) => <li key={it.id} className="flex justify-between gap-2"><span className="truncate">{it.descripcion}</span><span className="text-[10px]">{ESTADO_PARTIDA[est(it)]?.label}</span></li>)}</ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(false)} className="bg-transparent border-zinc-700">Cancelar</Button>
            <Button onClick={convertir} disabled={busy} className="bg-violet-500 text-zinc-950 hover:bg-violet-400" data-testid="partidas-confirm-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Crear petición con ${selItems.length} partida(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Bloque de origen en la Petición (Lead + Presupuesto + partidas) y CTA «Validar y lanzar a taller». */
export function OrigenPresupuestoParcial({ p, canValidar, onChanged }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const o = p.origen_presupuesto;
  if (!o) return null;
  const pendiente = p.estado === "pendiente_validacion";
  const validar = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/peticiones/${p.id}/validar-taller`);
      toast.success(`Lanzada a taller: ${data.lineas_creadas} línea(s), ${data.componentes_creados} componente(s)`);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo validar"); } finally { setBusy(false); }
  };
  return (
    <div className={`rounded-lg border p-3 mb-4 ${pendiente ? "border-amber-500/40 bg-amber-500/5" : "border-violet-500/30 bg-violet-500/5"}`} data-testid="origen-presupuesto-parcial">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-zinc-300 flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-violet-300">Origen</span>
          {o.lead_id && <button type="button" onClick={() => nav(`/leads?lead=${o.lead_id}`)} className="px-1.5 py-0.5 rounded border border-violet-500/40 hover:bg-violet-500/20" data-testid="origen-lead-link">Lead ↗</button>}
          <button type="button" onClick={() => nav(`/presupuestos/${o.presupuesto_id}`)} className="px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-200 hover:bg-amber-500/20 font-mono" data-testid="origen-presupuesto-link">{o.numero} ↗</button>
          <span className="text-zinc-500">{(o.partidas || []).length} partida(s){o.convertido_por ? ` · por ${o.convertido_por}` : ""}{o.convertido_at ? ` · ${o.convertido_at.slice(0, 10)}` : ""}</span>
        </div>
        {pendiente && canValidar && (
          <Button size="sm" onClick={validar} disabled={busy} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 h-8 font-semibold" data-testid="validar-taller-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Factory className="w-3.5 h-3.5 mr-1" /> Validar y lanzar a taller</>}
          </Button>
        )}
      </div>
      {pendiente && (
        <div className="mt-2 text-xs">
          <div className="text-amber-200 mb-1">Pendiente de validación: revisa las partidas antes de crear líneas y componentes. No dispara producción, rutas ni facturación.</div>
          <ul className="text-zinc-300 space-y-0.5">{(o.partidas || []).map((it) => <li key={it.id} className="flex justify-between gap-2"><span className="truncate">{it.descripcion}</span><span className="text-zinc-500 shrink-0">{it.cantidad} × {fmt(it.precio_unitario)}</span></li>)}</ul>
        </div>
      )}
    </div>
  );
}
